import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useId, useRef, useState } from "react";
import { toast } from "sonner";

import { Avatar } from "@/components/avatar";
import { Button } from "@/components/ui/button";
import { ConfirmDialog, Modal } from "@/components/ui/dialog";
import { Field, Form, Input } from "@/components/ui/field";
import { FkSelect } from "@/components/ui/fk-select";
import { LoadingNote } from "@/components/ui/loading";
import { UnitInput } from "@/components/ui/unit-input";
import { Skeleton } from "@/components/ui/skeleton";
import type { TranslationKey } from "@/constants/i18n";
import { api, errorMessage } from "@/services/api";
import { queryKeys } from "@/services/queries";
import { useDelayed } from "@/hooks/use-delayed";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";

/**
 * One session policy at three levels — organisation, role, person — the way
 * the API resolves them: the person's own value wins, then the strictest of
 * their roles, then the organisation. An empty box means "Default": inherit.
 *
 * Idle time and password expiry are a screen each, as the old system had them:
 * the component is given the field it edits and says nothing about the other.
 * They share one resource — `GET /access-policies` brings every level in one
 * call, and `PUT /access-policies` writes only the fields sent, so a screen
 * saving its own field leaves the other's alone.
 */

type PolicyField = "idle_timeout_minutes" | "password_change_interval_days";

/** What each of the two screens calls itself, and what its boxes are counted in. */
/**
 * The codename that may change each field.
 *
 * `/access-policies` refuses a write for a field the caller does not hold this
 * for, so a box left live to somebody without it is a box whose save comes back
 * 403 — the number goes in, the toast says no, and the old value drops back.
 * Disabled says the same thing before the typing.
 */
const CHANGE_PERMISSION: Record<PolicyField, string> = {
  idle_timeout_minutes: "change__idle_time",
  password_change_interval_days: "change__password_expiry",
};

const FIELD_COPY: Record<
  PolicyField,
  {
    title: TranslationKey;
    text: TranslationKey;
    unit: TranslationKey;
    globalText: TranslationKey;
    roleText: TranslationKey;
    addText: TranslationKey;
    /** The field named inside a sentence, where a heading's capitals would jar. */
    lower: TranslationKey;
  }
> = {
  idle_timeout_minutes: {
    title: "cfg.idleTitle",
    text: "cfg.idleText",
    unit: "cfg.minutes",
    globalText: "policy.globalIdleText",
    roleText: "policy.roleIdleText",
    addText: "policy.addIdleText",
    lower: "cfg.idleLower",
  },
  password_change_interval_days: {
    title: "cfg.passwordExpiry",
    text: "cfg.expiryText",
    unit: "cfg.days",
    globalText: "policy.globalExpiryText",
    roleText: "policy.roleExpiryText",
    addText: "policy.addExpiryText",
    lower: "cfg.expiryLower",
  },
};

interface Organization {
  id: number;
  name: string;
  idle_timeout_minutes: number | null;
  password_change_interval_days: number | null;
}

/** A subject joined to whatever policy it holds; policy_id is null when it inherits. */
interface PolicyValues {
  policy_id: number | null;
  idle_timeout_minutes: number | null;
  password_change_interval_days: number | null;
}

interface GroupRow extends PolicyValues {
  group_id: number;
  group_name: string;
}

interface UserRow extends PolicyValues {
  user_id: number;
  user_name: string;
  full_name: string;
  avatar: string | null;
}

interface Screen {
  organization: Organization | null;
  groups: GroupRow[];
  /** Only the people with a policy of their own. */
  users: UserRow[];
}

const SCREEN_KEY = ["/access-policies"] as const;

export function PolicyLevels({ field }: { field: PolicyField }) {
  const t = useTranslation();
  const allow = useCan();
  const editable = allow(CHANGE_PERMISSION[field]);
  const copy = FIELD_COPY[field];
  const title = t(copy.title);
  const unit = t(copy.unit);
  const queryClient = useQueryClient();

  const screen = useQuery({
    queryKey: SCREEN_KEY,
    queryFn: async ({ signal }) => (await api.get<Screen>("/access-policies", { signal })).data,
  });
  const org = screen.data?.organization ?? null;
  // A refetch keeps the rows on screen; the skeletons above are for the first
  // read only. Delayed, so a fast answer shows nothing at all.
  const busy = screen.isFetching;
  const refreshing = useDelayed(busy && !screen.isPending);

  /**
   * The change waiting on an answer: whose value, and what it would become.
   *
   * A box does not save itself. Leaving it with a new number asks first, and
   * only the answer writes — one subject, one field, one request. The screen
   * holds no unsaved state of its own, so there is nothing to lose by leaving
   * it and nothing to remember to press.
   */
  const [pending, setPending] = useState<{
    subject: { kind: "org" | "group" | "user"; id?: number };
    /** Who the row is about, as the question names them. */
    name: string;
    value: number | null;
  } | null>(null);
  /** Bumped when a change is refused: every box goes back to what the API holds. */
  const [reverted, setReverted] = useState(0);

  const refuse = () => {
    setPending(null);
    setReverted((n) => n + 1);
  };

  const commit = useMutation({
    mutationFn: async (change: NonNullable<typeof pending>) =>
      (
        await api.put<Screen>(
          "/access-policies",
          change.subject.kind === "org"
            ? { organization: { [field]: change.value } }
            : {
                policies: [
                  {
                    [change.subject.kind === "group" ? "group_id" : "user_id"]:
                      change.subject.id,
                    [field]: change.value,
                  },
                ],
              },
        )
      ).data,
    onSuccess: (next) => {
      toast.success(t("common.saved"));
      // The answer is the screen as saved; the shell's resolved copy refetches.
      queryClient.setQueryData(SCREEN_KEY, next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.effectivePolicy });
      setPending(null);
    },
    onError: (error) => {
      toast.error(errorMessage(error, t("common.couldNotSave")));
      // Refused by the server is still refused: the box goes back.
      refuse();
    },
  });

  /** A box left with a new value asks about it rather than taking it. */
  const ask =
    (subject: { kind: "org" | "group" | "user"; id?: number }, name: string) =>
    (value: number | null) =>
      setPending({ subject, name, value });

  const question =
    pending === null
      ? ""
      : pending.value === null
        ? t("policy.clearAsk").replace("{name}", pending.name).replace("{what}", t(copy.lower))
        : t("policy.updateAsk")
            .replace("{what}", t(copy.lower))
            .replace("{name}", pending.name)
            .replace("{value}", String(pending.value))
            .replace("{unit}", unit.toLowerCase());

  const [adding, setAdding] = useState(false);
  // Only the people who hold a policy of their own; the dialog writes a new one
  // straight away, so there is no half-added row to keep here.
  const individuals = screen.data?.users ?? [];

  return (
    <div>
      <h2 className="text-title-2 text-heading">{title}</h2>
      <p className="text-fg-muted text-callout mt-1">{t(copy.text)}</p>
      <div
        aria-busy={busy || undefined}
        className={cn(
          "transition-opacity duration-[var(--duration-base)]",
          busy && !screen.isPending && "opacity-55",
        )}
      >
        {/* No refresh button: every change here is written the moment it is
            confirmed and the answer brings the screen back with it, so there is
            nothing a reload would show that the page does not already hold. A
            read still in flight dims the levels, and the note beside this
            heading says so — beside it, so it costs the page no height. */}
        <div className="mt-5 flex items-center justify-between gap-4">
          <h3 className="text-heading text-callout font-medium">
            {t("policy.global").replace("{title}", title)}
          </h3>
          {refreshing ? <LoadingNote /> : null}
        </div>
        <div className="bg-surface-raised rounded-card mt-4 shadow-[var(--shadow-lift)]">
          {screen.isPending ? (
            <RowSkeleton />
          ) : org === null ? (
            <CreateOrganization
              field={field}
              label={title}
              unit={unit}
              disabled={!editable}
              onCreated={() => void queryClient.invalidateQueries({ queryKey: SCREEN_KEY })}
            />
          ) : (
            <LevelRow
              title={t("policy.system")}
              text={t(copy.globalText)}
              name={title}
              unit={unit}
              value={org[field]}
              onChange={ask({ kind: "org" }, t("policy.system"))}
              reverted={reverted}
              disabled={!editable}
              required
            />
          )}
        </div>

        <h3 className="text-heading text-callout mt-8 font-medium">
          {t("policy.roleBased").replace("{title}", title)}
        </h3>
        <div className="mt-4 grid gap-3">
          {screen.isPending
            ? Array.from({ length: 2 }, (_, index) => (
                <div
                  key={index}
                  className="bg-surface-raised rounded-card shadow-[var(--shadow-lift)]"
                >
                  <RowSkeleton />
                </div>
              ))
            : null}
          {(screen.data?.groups ?? []).map((row) => (
            <div
              key={row.group_id}
              className="bg-surface-raised rounded-card shadow-[var(--shadow-lift)]"
            >
              <LevelRow
                title={row.group_name}
                text={t(copy.roleText).replace("{role}", row.group_name)}
                name={title}
                unit={unit}
                value={row[field]}
                onChange={ask({ kind: "group", id: row.group_id }, row.group_name)}
                reverted={reverted}
                disabled={!editable}
              />
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <h3 className="text-heading text-callout font-medium">
            {t("policy.individual").replace("{title}", title)}
          </h3>
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={screen.isFetching || !editable}
            className="text-accent hover:bg-accent-soft text-callout inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium transition-colors disabled:pointer-events-none disabled:opacity-50"
          >
            <Plus className="size-4" aria-hidden /> {t("policy.addEmployee")}
          </button>
        </div>
        {adding ? (
          <AddPerson
            field={field}
            title={title}
            unit={unit}
            text={t(copy.addText)}
            taken={individuals.map((person) => person.user_id)}
            onClose={() => setAdding(false)}
          />
        ) : null}
        <ul className="divide-line mt-3 max-h-[26rem] divide-y overflow-y-auto">
          {individuals.map((row) => {
            const name = row.full_name || row.user_name;
            return (
              <li key={row.user_id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar src={row.avatar} name={name} className="bg-accent-soft text-accent" />
                  <span className="text-fg text-callout truncate">{name}</span>
                </span>
                <ValueInput
                  label={`${name}: ${title}`}
                  unit={unit}
                  value={row[field]}
                  onChange={ask({ kind: "user", id: row.user_id }, name)}
                  reverted={reverted}
                  disabled={!editable}
                />
              </li>
            );
          })}
          {screen.isPending ? (
            <li>
              <RowSkeleton />
            </li>
          ) : null}
          {!screen.isPending && individuals.length === 0 ? (
            <li className="text-fg-muted text-callout py-6 text-center">
              {t("policy.nobody")}
            </li>
          ) : null}
        </ul>
      </div>

      {/* The question a box asks on its way out: taken, or put back. */}
      <ConfirmDialog
        open={pending !== null}
        onClose={refuse}
        onConfirm={() => pending !== null && commit.mutate(pending)}
        tone="warning"
        title={t("policy.updateTitle").replace("{title}", title)}
        message={question}
        confirmLabel={t("common.update")}
        cancelLabel={t("common.cancel")}
        loading={commit.isPending}
      />
    </div>
  );
}

/**
 * A `LevelRow`'s outline, for while the screen loads: the title, its line of
 * explanation, and the two value boxes, in the space they will take.
 */
function RowSkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4">
      <div className="grid min-w-0 flex-1 basis-64 gap-2">
        <Skeleton className="w-40 max-w-full" />
        <Skeleton className="h-3 w-64 max-w-full" />
      </div>
      <Skeleton className="ml-auto h-9 w-28 shrink-0 rounded-lg" />
    </div>
  );
}

/** One level: who it applies to on the left, its single value on the right. */
function LevelRow({
  title,
  text,
  name,
  unit,
  value,
  onChange,
  reverted,
  required,
  disabled,
}: {
  title: string;
  text: string;
  /** What the box holds, for anyone who cannot see the heading above it. */
  name: string;
  unit: string;
  value: number | null;
  onChange: (value: number | null) => void;
  /** Changes when a change was refused, which puts the box back. */
  reverted: number;
  required?: boolean;
  /** Read-only: the reader may see this policy but not set it. */
  disabled?: boolean;
}) {
  return (
    // Wraps before it overflows: on a narrow column the box drops under the
    // text rather than pushing the page sideways, and `ml-auto` keeps it at the
    // right edge either way.
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4">
      <div className="min-w-0 flex-1 basis-64">
        <p className="text-heading text-callout font-semibold break-words">{title}</p>
        <p className="text-fg-muted text-subhead mt-0.5 leading-[18px] break-words">{text}</p>
      </div>
      <ValueInput
        label={`${title}: ${name}`}
        unit={unit}
        value={value}
        onChange={onChange}
        reverted={reverted}
        required={required}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Commits on blur or Enter; an emptied box means inherit. The screen's heading
 * already says which value this is, so the label is there for assistive
 * technology only and the unit is what stays on screen beside the box.
 *
 * Enter asks the question where the box stands rather than blurring to get
 * there. Blurring works — the question is the same one — but it drops focus on
 * the way, so the dialog has nowhere to hand it back to and answering from the
 * keyboard leaves the caret at the top of the page instead of in the box that
 * was just typed in.
 */
function ValueInput({
  label,
  unit,
  value,
  onChange,
  reverted,
  required,
  disabled,
}: {
  label: string;
  unit: string;
  value: number | null;
  onChange: (value: number | null) => void;
  /** Changes when a change was refused, which puts the box back. */
  reverted: number;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
}) {
  const t = useTranslation();
  const [draft, setDraft] = useState(value?.toString() ?? "");
  const [seen, setSeen] = useState(value);
  const [seenRevert, setSeenRevert] = useState(reverted);
  // The value already put to the person — `null` is one of them, so the ref
  // holds a box rather than the number. Opening the question takes focus out of
  // the box, and the blur that follows must not ask the same thing twice.
  // Cleared by the next keystroke, which is the only way a new value arrives.
  const asked = useRef<{ value: number | null } | null>(null);
  // A save elsewhere (or a refetch) moves the truth; follow it without an
  // effect. A refused change moves nothing, so the box is put back by hand.
  if (seen !== value || seenRevert !== reverted) {
    setSeen(value);
    setSeenRevert(reverted);
    setDraft(value?.toString() ?? "");
  }
  const commit = () => {
    const next = draft.trim() === "" ? null : Number(draft);
    if (next === value) return;
    if (required && next === null) {
      setDraft(value?.toString() ?? "");
      return;
    }
    if (asked.current?.value === next) return;
    asked.current = { value: next };
    onChange(next);
  };
  return (
    <label className="text-callout ml-auto shrink-0">
      <span className="sr-only">{label}</span>
      <UnitInput
        unit={unit}
        type="number"
        min={1}
        inputMode="numeric"
        disabled={disabled}
        value={draft}
        placeholder={t("policy.default")}
        onChange={(e) => {
          setDraft(e.target.value);
          asked.current = null;
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          commit();
        }}
        // A row holds a number of three or four digits, so the box is sized to
        // one rather than to the column it sits in.
        className="h-9 w-auto"
        inputClassName="w-16 flex-none px-2 text-center"
      />
    </label>
  );
}

/**
 * Adding one person their own value: who, and what it is.
 *
 * It saves that person on its own rather than staging a blank row for the
 * footer to write — an employee added but left at "Default" is a row of nulls,
 * which is not a policy, so the pair is asked for together and written
 * together. Edits already on the screen are untouched: the request carries
 * this person only.
 *
 * Two fields and a Save is short enough that Enter means "that one, go" — so
 * the pair is a form and Save submits it. The picker keeps Enter for itself:
 * its trigger is a button and its search box stops the key at the list, so the
 * only Enter that reaches the form is the one pressed in the number.
 */
function AddPerson({
  field,
  title,
  unit,
  text,
  taken,
  onClose,
}: {
  field: PolicyField;
  /** What the value is called — the screen's own heading. */
  title: string;
  unit: string;
  text: string;
  /** People who already hold a policy, kept out of the picker. */
  taken: number[];
  onClose: () => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const formId = useId();
  const [person, setPerson] = useState("");
  const [value, setValue] = useState("");
  const ready = person !== "" && value.trim() !== "";

  const save = useMutation({
    mutationFn: async () =>
      (
        await api.put<Screen>("/access-policies", {
          policies: [{ user_id: Number(person), [field]: Number(value) }],
        })
      ).data,
    onSuccess: (next) => {
      toast.success(t("common.saved"));
      queryClient.setQueryData(SCREEN_KEY, next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.effectivePolicy });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t("common.couldNotSave"))),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t("policy.addPersonTitle").replace("{title}", title)}
      description={text}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={!ready}
            loading={save.isPending}
            loadingLabel={t("common.saving")}
          >
            {t("rp.save")}
          </Button>
        </>
      }
    >
      <Form
        id={formId}
        className="grid gap-4 pb-2"
        /**
         * Save sits in the sheet's footer — outside this form, joined to it by
         * `form=` — so Enter is asked for by hand rather than left to the
         * browser's implicit submit, which not every engine grants a form whose
         * only submit button is not inside it. Buttons keep Enter's own
         * meaning: the picker opens on it.
         */
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.shiftKey) return;
          if ((event.target as HTMLElement).tagName.toLowerCase() === "button") return;
          event.preventDefault();
          event.currentTarget.requestSubmit();
        }}
        onSubmit={(event) => {
          event.preventDefault();
          // Enter from a half-filled pair is not a save; Save says so too.
          if (!ready || save.isPending) return;
          save.mutate();
        }}
      >
        <Field label={t("policy.employee")} htmlFor="add-person">
          <FkSelect
            id="add-person"
            endpoint="/users/compact"
            value={person}
            placeholder={t("policy.chooseEmployee")}
            exclude={(option) => taken.includes(option.id)}
            onChange={(next) => setPerson(next)}
          />
        </Field>
        <Field label={title} htmlFor="add-person-value">
          <UnitInput
            id="add-person-value"
            unit={unit}
            type="number"
            min={1}
            inputMode="numeric"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </Field>
      </Form>
    </Modal>
  );
}

/**
 * No organisation row yet: the one place it gets created, with a name and this
 * screen's own value. The other screen's value is left unset — it is set there,
 * once the row exists.
 */
function CreateOrganization({
  field,
  label,
  unit,
  onCreated,
  disabled,
}: {
  field: PolicyField;
  label: string;
  unit: string;
  onCreated: () => void;
  /** Read-only: the reader may see this policy but not set it. */
  disabled?: boolean | undefined;
}) {
  const t = useTranslation();
  const [name, setName] = useState("S&R Membership Shopping");
  const [value, setValue] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.put("/organizations", {
        name,
        [field]: value ? Number(value) : null,
      }),
    onSuccess: () => {
      toast.success(t("policy.orgCreated"));
      onCreated();
    },
    onError: (error) => toast.error(errorMessage(error, t("policy.orgCreateFailed"))),
  });
  return (
    <Form
      className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_8rem_auto] md:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Field label={t("policy.orgName")} htmlFor="org-name" hint={t("policy.orgHint")}>
        <Input
          id="org-name"
          required
          disabled={disabled}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label={label} htmlFor="org-value">
        <UnitInput
          id="org-value"
          unit={unit}
          type="number"
          min={1}
          inputMode="numeric"
          disabled={disabled}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </Field>
      <Button
        type="submit"
        size="lg"
        disabled={disabled}
        loading={create.isPending}
        loadingLabel={t("common.saving")}
      >
        {t("common.create")}
      </Button>
    </Form>
  );
}
